import { COORDINATION_BASE_ROLES } from '@arcexperts/core-types'
import { useCoordination } from '@/helpers/coordination'
import { defineStore, type StoreDefinition } from 'pinia'
import type { DefaultObject, NestedObject } from '@/types'
import { useProjectStore } from '@/stores/project'
import { useClientStore } from '@/stores/client'
import { useProjectGeneralCalculator } from '@/stores/projectCenter/projectGeneralCalculator'
import {
  useProjectArchitectCalculator,
  useProjectLandscapeArchitectCalculator,
  useProjectCivilEngineerCalculator,
  useProjectCivilEngineerExcavationCalculator,
  useProjectEngineerCalculator,
  useProjectElectricalCalculator,
  useProjectHeatingCoolingCalculator,
  useProjectVentilationACCalculator,
  useProjectHVACCalculator,
  useProjectSanitaryCalculator,
  useProjectBuildingAutomationCalculator,
  useProjectSpecialistCoordinationCalculator,
} from '@/stores/projectCenter/projectServiceCalculatorFactory'
import { buildStoredServiceResultsV2 } from '@/helpers/service-results'
import { countriesWithLocation } from '@/validation/field-validators'
import { isClientSectionHidden } from '@/helpers/client-feature-flags'
import type { SocketProjectCalculationResultData } from '@arcexperts/core-types'

const SETUP_PARTS = [
  'architect_setup',
  'landscape_architect_setup',
  'civil_engineer_setup',
  'civil_engineer_excavation_setup',
  'electrical_setup',
  'heating_cooling_setup',
  'ventilation_ac_setup',
  'hvac_setup',
  'sanitary_setup',
  'building_automation_setup',
  'specialist_coordination_setup',
  'engineer_setup',
] as const
const LANDSCAPE_ARCHITECT_ROLE = 'LANDSCAPE_ARCHITECT'

function hasPositiveAreaAndUsages(
  data: Record<string, unknown> | undefined,
  quantityAreaKey: 'quantityArea' | 'landscapeQuantityArea',
  usageItemsKey: 'usageItems' | 'landscapeUsageItems'
): boolean {
  const usageItems = data?.[usageItemsKey] as unknown[] | undefined
  const quantityArea = data?.[quantityAreaKey] as number | undefined
  return !!((quantityArea ?? 0) > 0 && usageItems?.length)
}

function getAssignedProjectRoles(): string[] {
  const projectStore = useProjectStore()
  return projectStore.getValidRoles ?? []
}

function hasStandardProjectRole(roles: string[]): boolean {
  return roles.some((role) => role !== LANDSCAPE_ARCHITECT_ROLE)
}

export interface State {
  project: DefaultObject | null
  generalCalculator: ReturnType<typeof useProjectGeneralCalculator>
  architectCalculator: ReturnType<typeof useProjectArchitectCalculator>
  landscapeArchitectCalculator: ReturnType<typeof useProjectLandscapeArchitectCalculator>
  civilEngineerCalculator: ReturnType<typeof useProjectCivilEngineerCalculator>
  civilEngineerExcavationCalculator: ReturnType<typeof useProjectCivilEngineerExcavationCalculator>
  engineerCalculator: ReturnType<typeof useProjectEngineerCalculator>
  electricalCalculator: ReturnType<typeof useProjectElectricalCalculator>
  heatingCoolingCalculator: ReturnType<typeof useProjectHeatingCoolingCalculator>
  ventilationACCalculator: ReturnType<typeof useProjectVentilationACCalculator>
  hvacCalculator: ReturnType<typeof useProjectHVACCalculator>
  sanitaryCalculator: ReturnType<typeof useProjectSanitaryCalculator>
  buildingAutomationCalculator: ReturnType<typeof useProjectBuildingAutomationCalculator>
  specialistCoordinationCalculator: ReturnType<typeof useProjectSpecialistCoordinationCalculator>
}

export interface ProjectCalculatorGetters {
  getProject: (state: State) => DefaultObject | null
  checkValidity: (state: State) => (part: string, data: Record<string, unknown> | null | undefined) => boolean
}

export interface ProjectCalculatorActions {
  setProject(project: DefaultObject | null): void
  setProjectGeneralData(data?: NestedObject): void
  setProjectArchitectSetupData(data?: NestedObject): void
  setProjectArchitectServiceData(data?: NestedObject): void
  setProjectLandscapeArchitectSetupData(data?: NestedObject): void
  setProjectLandscapeArchitectServiceData(data?: NestedObject): void
  setProjectCivilEngineerSetupData(data?: NestedObject): void
  setProjectCivilEngineerServiceData(data?: NestedObject): void
  setProjectCivilEngineerExcavationSetupData(data?: NestedObject): void
  setProjectCivilEngineerExcavationServiceData(data?: NestedObject): void
  setProjectElectricalSetupData(data?: NestedObject): void
  setProjectElectricalServiceData(data?: NestedObject): void
  setProjectHeatingCoolingSetupData(data?: NestedObject): void
  setProjectHeatingCoolingServiceData(data?: NestedObject): void
  setProjectVentilationACSetupData(data?: NestedObject): void
  setProjectVentilationACServiceData(data?: NestedObject): void
  setProjectHVACSetupData(data?: NestedObject): void
  setProjectHVACServiceData(data?: NestedObject): void
  setProjectSanitarySetupData(data?: NestedObject): void
  setProjectSanitaryServiceData(data?: NestedObject): void
  setProjectBuildingAutomationSetupData(data?: NestedObject): void
  setProjectBuildingAutomationServiceData(data?: NestedObject): void
  setProjectSpecialistCoordinationSetupData(data?: NestedObject): void
  setProjectSpecialistCoordinationServiceData(data?: NestedObject): void
  setProjectEngineerSetupData(data?: NestedObject): void
  setProjectEngineerServiceData(data?: NestedObject): void
  setResult(result: SocketProjectCalculationResultData): void
}

export const useProjectCalculator: StoreDefinition<
  'projectCalculator',
  State,
  ProjectCalculatorGetters,
  ProjectCalculatorActions
> = defineStore('projectCalculator', {
  state: (): State => ({
    project: null,
    generalCalculator: useProjectGeneralCalculator(),
    architectCalculator: useProjectArchitectCalculator(),
    landscapeArchitectCalculator: useProjectLandscapeArchitectCalculator(),
    civilEngineerCalculator: useProjectCivilEngineerCalculator(),
    civilEngineerExcavationCalculator: useProjectCivilEngineerExcavationCalculator(),
    engineerCalculator: useProjectEngineerCalculator(),
    electricalCalculator: useProjectElectricalCalculator(),
    heatingCoolingCalculator: useProjectHeatingCoolingCalculator(),
    ventilationACCalculator: useProjectVentilationACCalculator(),
    hvacCalculator: useProjectHVACCalculator(),
    sanitaryCalculator: useProjectSanitaryCalculator(),
    buildingAutomationCalculator: useProjectBuildingAutomationCalculator(),
    specialistCoordinationCalculator: useProjectSpecialistCoordinationCalculator(),
  }),
  getters: {
    getProject: (state: State) => state.project,
    checkValidity:
      (state: State) =>
      (part: string, data: Record<string, unknown> | null | undefined): boolean => {
        if (!data) return false
        if (part === 'general') {
          const location = data.location as Record<string, unknown> | undefined
          const country = location?.country as string | undefined
          // TEMPORARY: Skip location check for clients with hidden location section
          const clientStore = useClientStore()
          const isRestrictedClient = isClientSectionHidden(clientStore.getUser?.name, 'location')
          const hasLocation =
            isRestrictedClient || !!(country && countriesWithLocation.includes(country) ? data.canton : true)
          const roles = getAssignedProjectRoles()
          const needsStandardUsages = hasStandardProjectRole(roles)
          const needsLandscapeUsages = roles.includes(LANDSCAPE_ARCHITECT_ROLE)
          const hasRequiredStandardUsages =
            !needsStandardUsages || hasPositiveAreaAndUsages(data, 'quantityArea', 'usageItems')
          const hasRequiredLandscapeUsages =
            !needsLandscapeUsages || hasPositiveAreaAndUsages(data, 'landscapeQuantityArea', 'landscapeUsageItems')
          return hasLocation && hasRequiredStandardUsages && hasRequiredLandscapeUsages
        }
        if (part === 'specialist_coordination_setup' && useCoordination().enabled.value) return true
        if (SETUP_PARTS.includes(part as (typeof SETUP_PARTS)[number])) {
          // Setup validity now depends on shared usages from general
          const generalData = state.project?.general?.data as Record<string, unknown> | undefined
          if (part === 'landscape_architect_setup') {
            return hasPositiveAreaAndUsages(generalData, 'landscapeQuantityArea', 'landscapeUsageItems')
          }
          return hasPositiveAreaAndUsages(generalData, 'quantityArea', 'usageItems')
        }
        return true
      },
  },
  actions: {
    setProject(project: DefaultObject | null): void {
      /* called from projectPageBase on each route */
      this.$patch({ project: project })

      // Get roles from meta - these are the roles that need calculations
      // Parts are for UI display only (subset of roles)
      // No fallback - if client has no roles configured, they have no access
      const meta = useProjectStore().getMeta
      const roles: string[] = meta?.roles ?? []

      // Initialize general calculator only if client has at least one role
      if (roles.length) {
        this.generalCalculator.init(this.project?.general?.data)
      }

      // Initialize role calculators only for assigned roles
      if (roles.includes('ARCHITECT')) {
        this.architectCalculator.initSetupData(this.project?.architect_setup?.data)
        this.architectCalculator.initServiceData(this.project?.architect_service?.data)
      }

      if (roles.includes('LANDSCAPE_ARCHITECT')) {
        this.landscapeArchitectCalculator.initSetupData(this.project?.landscape_architect_setup?.data)
        this.landscapeArchitectCalculator.initServiceData(this.project?.landscape_architect_service?.data)
      }

      if (roles.includes('CIVIL_ENGINEER')) {
        this.civilEngineerCalculator.initSetupData(this.project?.civil_engineer_setup?.data)
        this.civilEngineerCalculator.initServiceData(this.project?.civil_engineer_service?.data)
      }

      if (roles.includes('CIVIL_ENGINEER_EXCAVATION')) {
        this.civilEngineerExcavationCalculator.initSetupData(this.project?.civil_engineer_excavation_setup?.data)
        this.civilEngineerExcavationCalculator.initServiceData(this.project?.civil_engineer_excavation_service?.data)
      }

      if (roles.includes('ENGINEER')) {
        if (this.project?.engineer_setup) {
          this.engineerCalculator.initSetupData(this.project?.engineer_setup.data)
        }
        if (this.project?.engineer_service) {
          this.engineerCalculator.initServiceData(this.project?.engineer_service.data)
        }
      }

      if (roles.includes('ELECTRICAL')) {
        if (this.project?.electrical_setup) {
          this.electricalCalculator.initSetupData(this.project?.electrical_setup.data)
        }
        if (this.project?.electrical_service) {
          this.electricalCalculator.initServiceData(this.project?.electrical_service.data)
        }
      }

      if (roles.includes('HEATING_COOLING')) {
        this.heatingCoolingCalculator.initSetupData(this.project?.heating_cooling_setup?.data)
        this.heatingCoolingCalculator.initServiceData(this.project?.heating_cooling_service?.data)
      }

      if (roles.includes('VENTILATION_AC')) {
        this.ventilationACCalculator.initSetupData(this.project?.ventilation_ac_setup?.data)
        this.ventilationACCalculator.initServiceData(this.project?.ventilation_ac_service?.data)
      }

      if (roles.includes('HVAC')) {
        if (this.project?.hvac_setup) {
          this.hvacCalculator.initSetupData(this.project?.hvac_setup.data)
        }
        if (this.project?.hvac_service) {
          this.hvacCalculator.initServiceData(this.project?.hvac_service.data)
        }
      }

      if (roles.includes('BUILDING_AUTOMATION')) {
        this.buildingAutomationCalculator.initSetupData(this.project?.building_automation_setup?.data)
        this.buildingAutomationCalculator.initServiceData(this.project?.building_automation_service?.data)
      }

      if (roles.includes('SPECIALIST_COORDINATION')) {
        this.specialistCoordinationCalculator.initSetupData(this.project?.specialist_coordination_setup?.data)
        this.specialistCoordinationCalculator.initServiceData(this.project?.specialist_coordination_service?.data)
      }

      if (roles.includes('SANITARY')) {
        if (this.project?.sanitary_setup) {
          this.sanitaryCalculator.initSetupData(this.project?.sanitary_setup.data)
        }
        if (this.project?.sanitary_service) {
          this.sanitaryCalculator.initServiceData(this.project?.sanitary_service.data)
        }
      }
    },
    setProjectGeneralData(data?: NestedObject) {
      /* called on each model change */
      this.generalCalculator.setGeneralData(data)
    },
    setProjectArchitectSetupData(data?: NestedObject) {
      /* called on each model change */
      this.architectCalculator.setSetupData(data)
    },
    setProjectArchitectServiceData(data?: NestedObject) {
      /* called on each model change */
      this.architectCalculator.setServiceData(data)
    },
    setProjectLandscapeArchitectSetupData(data?: NestedObject) {
      this.landscapeArchitectCalculator.setSetupData(data)
    },
    setProjectLandscapeArchitectServiceData(data?: NestedObject) {
      this.landscapeArchitectCalculator.setServiceData(data)
    },
    setProjectCivilEngineerSetupData(data?: NestedObject) {
      this.civilEngineerCalculator.setSetupData(data)
    },
    setProjectCivilEngineerServiceData(data?: NestedObject) {
      this.civilEngineerCalculator.setServiceData(data)
    },
    setProjectCivilEngineerExcavationSetupData(data?: NestedObject) {
      this.civilEngineerExcavationCalculator.setSetupData(data)
    },
    setProjectCivilEngineerExcavationServiceData(data?: NestedObject) {
      this.civilEngineerExcavationCalculator.setServiceData(data)
    },
    setProjectElectricalSetupData(data?: NestedObject) {
      /* called on each model change */
      this.electricalCalculator.setSetupData(data)
    },
    setProjectElectricalServiceData(data?: NestedObject) {
      /* called on each model change */
      this.electricalCalculator.setServiceData(data)
    },
    setProjectHeatingCoolingSetupData(data?: NestedObject) {
      this.heatingCoolingCalculator.setSetupData(data)
    },
    setProjectHeatingCoolingServiceData(data?: NestedObject) {
      this.heatingCoolingCalculator.setServiceData(data)
    },
    setProjectVentilationACSetupData(data?: NestedObject) {
      this.ventilationACCalculator.setSetupData(data)
    },
    setProjectVentilationACServiceData(data?: NestedObject) {
      this.ventilationACCalculator.setServiceData(data)
    },
    setProjectHVACSetupData(data?: NestedObject) {
      /* called on each model change */
      this.hvacCalculator.setSetupData(data)
    },
    setProjectHVACServiceData(data?: NestedObject) {
      /* called on each model change */
      this.hvacCalculator.setServiceData(data)
    },
    setProjectSanitarySetupData(data?: NestedObject) {
      /* called on each model change */
      this.sanitaryCalculator.setSetupData(data)
    },
    setProjectSanitaryServiceData(data?: NestedObject) {
      /* called on each model change */
      this.sanitaryCalculator.setServiceData(data)
    },
    setProjectBuildingAutomationSetupData(data?: NestedObject) {
      this.buildingAutomationCalculator.setSetupData(data)
    },
    setProjectBuildingAutomationServiceData(data?: NestedObject) {
      this.buildingAutomationCalculator.setServiceData(data)
    },
    setProjectSpecialistCoordinationSetupData(data?: NestedObject) {
      this.specialistCoordinationCalculator.setSetupData(data)
    },
    setProjectSpecialistCoordinationServiceData(data?: NestedObject) {
      this.specialistCoordinationCalculator.setServiceData(data)
    },
    setProjectEngineerSetupData(data?: NestedObject) {
      this.engineerCalculator.setSetupData(data)
    },
    setProjectEngineerServiceData(data?: NestedObject) {
      this.engineerCalculator.setServiceData(data)
    },
    setResult(result: SocketProjectCalculationResultData) {
      if (result.part === 'general') {
        this.generalCalculator.setResult(result)
      }
      if (result.part === 'architect_setup' || result.part === 'architect_service') {
        this.architectCalculator.setResult(result)
      }
      if (result.part === 'landscape_architect_setup' || result.part === 'landscape_architect_service') {
        this.landscapeArchitectCalculator.setResult(result)
      }
      if (result.part === 'civil_engineer_setup' || result.part === 'civil_engineer_service') {
        this.civilEngineerCalculator.setResult(result)
      }
      if (result.part === 'civil_engineer_excavation_setup' || result.part === 'civil_engineer_excavation_service') {
        this.civilEngineerExcavationCalculator.setResult(result)
      }
      if (result.part === 'engineer_setup' || result.part === 'engineer_service') {
        this.engineerCalculator.setResult(result)
      }
      if (result.part === 'electrical_setup' || result.part === 'electrical_service') {
        this.electricalCalculator.setResult(result)
      }
      if (result.part === 'heating_cooling_setup' || result.part === 'heating_cooling_service') {
        this.heatingCoolingCalculator.setResult(result)
      }
      if (result.part === 'ventilation_ac_setup' || result.part === 'ventilation_ac_service') {
        this.ventilationACCalculator.setResult(result)
      }
      if (result.part === 'hvac_setup' || result.part === 'hvac_service') {
        this.hvacCalculator.setResult(result)
      }
      if (result.part === 'sanitary_setup' || result.part === 'sanitary_service') {
        this.sanitaryCalculator.setResult(result)
      }
      if (result.part === 'building_automation_setup' || result.part === 'building_automation_service') {
        this.buildingAutomationCalculator.setResult(result)
      }
      if (result.part === 'specialist_coordination_setup' || result.part === 'specialist_coordination_service') {
        this.specialistCoordinationCalculator.setResult(result)
      }

      const projectStore = useProjectStore()
      if (result.part.endsWith('_service')) {
        if (!['serviceHours', 'percentageRangeForPhases'].includes(result.request)) {
          return
        }

        const calculator =
          result.part === 'architect_service'
            ? this.architectCalculator
            : result.part === 'landscape_architect_service'
              ? this.landscapeArchitectCalculator
              : result.part === 'civil_engineer_service'
                ? this.civilEngineerCalculator
                : result.part === 'civil_engineer_excavation_service'
                  ? this.civilEngineerExcavationCalculator
                  : result.part === 'engineer_service'
                    ? this.engineerCalculator
                    : result.part === 'electrical_service'
                      ? this.electricalCalculator
                      : result.part === 'heating_cooling_service'
                        ? this.heatingCoolingCalculator
                        : result.part === 'ventilation_ac_service'
                          ? this.ventilationACCalculator
                          : result.part === 'hvac_service'
                            ? this.hvacCalculator
                            : result.part === 'building_automation_service'
                              ? this.buildingAutomationCalculator
                              : result.part === 'specialist_coordination_service'
                                ? this.specialistCoordinationCalculator
                                : this.sanitaryCalculator

        if (calculator.serviceHours && calculator.serviceData?.phases?.length) {
          projectStore.replaceCalculatedResults(
            result.part,
            buildStoredServiceResultsV2({
              estimatedTotalHours: calculator.estimatedTotalHours,
              phases: calculator.serviceData.phases,
              percentageRangeForPhases: calculator.percentageRangeForPhases,
              serviceHours: calculator.serviceHours,
            })
          )
        }
        return
      }

      projectStore.updateCalculatedResults(result.part, result.request, result.value)
      if (result.coordinationBasis)
        projectStore.updateCalculatedResults(result.part, 'coordinationBasis', result.coordinationBasis)
      if (
        result.request === 'estimatedTotalHours' &&
        COORDINATION_BASE_ROLES.some((role) => `${role.toLowerCase()}_setup` === result.part) &&
        projectStore.getValidRoles.includes('SPECIALIST_COORDINATION') &&
        useCoordination().enabled.value
      ) {
        this.specialistCoordinationCalculator.setEstimatedTotalHours()
      }
    },
  },
})
